import { useEffect, useMemo, useState } from 'react';
import Sheet from '../components/Sheet';
import BrandMark from '../components/BrandMark';
import { supabase } from '../lib/supabase';

const THIS_YEAR = new Date().getFullYear();

function slugify(value) {
  return value
    .trim()
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function yearLabel(from, to) {
  if (!from && !to) return 'Years not set';
  if (from && to) return `${from}–${to}`;
  return from ? `From ${from}` : `To ${to}`;
}

export default function BrandsCatalog({ isManager }) {
  const [makes, setMakes] = useState([]);
  const [query, setQuery] = useState('');
  const [catalogQuery, setCatalogQuery] = useState('');
  const [editId, setEditId] = useState('');
  const [modelQuery, setModelQuery] = useState('');
  const [error, setError] = useState('');
  const [modelDraft, setModelDraft] = useState('');
  const [brandDraft, setBrandDraft] = useState('');
  const [addingBrand, setAddingBrand] = useState(false);
  const [saving, setSaving] = useState(false);

  async function load() {
    const { data, error: queryError } = await supabase
      .from('vehicle_makes')
      .select('*, vehicle_models(*)')
      .order('sort_order')
      .order('name');
    if (queryError) {
      setError(queryError.message);
      return;
    }
    setMakes(
      (data || []).map((row) => ({
        ...row,
        vehicle_models: (row.vehicle_models || []).sort(
          (a, b) =>
            (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.name.localeCompare(b.name)
        ),
      }))
    );
  }

  useEffect(() => {
    load();
  }, []);

  const offered = useMemo(
    () => makes.filter((item) => item.is_offered),
    [makes]
  );
  const available = useMemo(
    () => makes.filter((item) => !item.is_offered),
    [makes]
  );

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return offered;
    return offered.filter((make) => {
      const hay = [make.name, ...(make.vehicle_models || []).map((item) => item.name)]
        .join(' ')
        .toLowerCase();
      return hay.includes(needle);
    });
  }, [offered, query]);

  const catalog = useMemo(() => {
    const needle = catalogQuery.trim().toLowerCase();
    if (!needle) return available;
    return available.filter((item) => item.name.toLowerCase().includes(needle));
  }, [available, catalogQuery]);

  const editing = makes.find((item) => item.id === editId);

  const editingModels = useMemo(() => {
    if (!editing) return [];
    const needle = modelQuery.trim().toLowerCase();
    const rows = editing.vehicle_models || [];
    if (!needle) return rows;
    return rows.filter((item) => item.name.toLowerCase().includes(needle));
  }, [editing, modelQuery]);

  async function patchMake(id, payload) {
    if (!isManager) return;
    setSaving(true);
    setError('');
    const { error: saveError } = await supabase
      .from('vehicle_makes')
      .update(payload)
      .eq('id', id);
    setSaving(false);
    if (saveError) {
      setError(saveError.message);
      return;
    }
    setMakes((current) =>
      current.map((item) => (item.id === id ? { ...item, ...payload } : item))
    );
  }

  async function offerMake(make) {
    await patchMake(make.id, {
      is_offered: true,
      year_from: make.year_from || 2000,
      year_to: make.year_to || THIS_YEAR + 1,
    });
    setAddingBrand(false);
    setCatalogQuery('');
    setEditId(make.id);
  }

  async function toggleModel(model, isOffered) {
    if (!isManager) return;
    const { error: saveError } = await supabase
      .from('vehicle_models')
      .update({ is_offered: isOffered })
      .eq('id', model.id);
    if (saveError) {
      setError(saveError.message);
      return;
    }
    setMakes((current) =>
      current.map((make) =>
        make.id === model.make_id
          ? {
              ...make,
              vehicle_models: make.vehicle_models.map((item) =>
                item.id === model.id ? { ...item, is_offered: isOffered } : item
              ),
            }
          : make
      )
    );
  }

  async function addModel(makeId) {
    const name = modelDraft.trim();
    if (!name || !isManager) return;
    const { error: saveError } = await supabase.from('vehicle_models').insert({
      make_id: makeId,
      name,
      is_offered: true,
      sort_order: 99,
    });
    if (saveError) {
      setError(saveError.message);
      return;
    }
    setModelDraft('');
    load();
  }

  async function addCustomBrand(event) {
    event.preventDefault();
    if (!isManager) return;
    const name = brandDraft.trim();
    if (!name) return;
    const slug = slugify(name);
    const { error: saveError } = await supabase.from('vehicle_makes').insert({
      slug: slug || `make-${Date.now()}`,
      name,
      is_offered: true,
      year_from: 2000,
      year_to: THIS_YEAR + 1,
      sort_order: 900,
    });
    if (saveError) {
      setError(saveError.message);
      return;
    }
    setBrandDraft('');
    setAddingBrand(false);
    load();
  }

  return (
    <>
      <div className="panel-head">
        <p className="lede" style={{ margin: 0, maxWidth: 520 }}>
          Offered brands appear in the app. Open a brand to set years and models.
        </p>
        {isManager && (
          <button type="button" className="primary" onClick={() => setAddingBrand(true)}>
            Offer a brand
          </button>
        )}
      </div>
      {error && <p className="form-error">{error}</p>}
      {offered.length > 0 && (
        <input
          className="search"
          placeholder="Search brands…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ marginBottom: 16 }}
        />
      )}
      {visible.length === 0 ? (
        <p className="empty">
          No offered brands yet. Offer a brand from the catalog to start.
        </p>
      ) : (
        <div className="booking-list">
          {visible.map((make) => {
            const offeredModels = (make.vehicle_models || []).filter((item) => item.is_offered);
            return (
              <button
                key={make.id}
                type="button"
                className="desk-row as-button"
                onClick={() => {
                  setEditId(make.id);
                  setModelQuery('');
                  setModelDraft('');
                }}
              >
                <BrandMark name={make.name} src={make.logo_url} compact />
                <div className="desk-main">
                  <h3>{make.name}</h3>
                  <p>
                    {offeredModels.length} of {(make.vehicle_models || []).length} models ·{' '}
                    {yearLabel(make.year_from, make.year_to)}
                  </p>
                </div>
                <span className="material-symbol desk-chevron">chevron_right</span>
              </button>
            );
          })}
        </div>
      )}
      {editing && (
        <Sheet
          variant="modal"
          wide
          title={editing.name}
          subtitle="Brand catalog"
          onClose={() => setEditId('')}
        >
          {isManager ? (
            <div className="form-row" style={{ marginBottom: 16 }}>
              <label>
                Year from
                <input
                  type="number"
                  min="1980"
                  max={THIS_YEAR + 2}
                  value={editing.year_from ?? ''}
                  onChange={(e) =>
                    setMakes((current) =>
                      current.map((item) =>
                        item.id === editing.id
                          ? { ...item, year_from: e.target.value }
                          : item
                      )
                    )
                  }
                  onBlur={(e) =>
                    patchMake(editing.id, {
                      year_from: Number(e.target.value) || 2000,
                    })
                  }
                />
              </label>
              <label>
                Year to
                <input
                  type="number"
                  min="1980"
                  max={THIS_YEAR + 2}
                  value={editing.year_to ?? ''}
                  onChange={(e) =>
                    setMakes((current) =>
                      current.map((item) =>
                        item.id === editing.id
                          ? { ...item, year_to: e.target.value }
                          : item
                      )
                    )
                  }
                  onBlur={(e) =>
                    patchMake(editing.id, {
                      year_to: Number(e.target.value) || THIS_YEAR + 1,
                    })
                  }
                />
              </label>
            </div>
          ) : (
            <p className="lede" style={{ marginTop: 0 }}>
              {yearLabel(editing.year_from, editing.year_to)}
            </p>
          )}
          <input
            className="search"
            placeholder="Filter models…"
            value={modelQuery}
            onChange={(e) => setModelQuery(e.target.value)}
            style={{ marginBottom: 12, maxWidth: 'none' }}
          />
          {(editing.vehicle_models || []).length === 0 ? (
            <p className="empty">No models yet.</p>
          ) : (
            <ul className="model-check-list">
              {editingModels.map((model) => (
                <li key={model.id}>
                  <label className={model.is_offered ? 'on' : ''}>
                    {isManager ? (
                      <input
                        type="checkbox"
                        checked={Boolean(model.is_offered)}
                        onChange={(e) => toggleModel(model, e.target.checked)}
                      />
                    ) : (
                      <span className="material-symbol" aria-hidden="true">
                        {model.is_offered ? 'check' : 'remove'}
                      </span>
                    )}
                    {model.name}
                  </label>
                </li>
              ))}
            </ul>
          )}
          {isManager && (
            <>
              <form
                className="model-add"
                onSubmit={(event) => {
                  event.preventDefault();
                  addModel(editing.id);
                }}
              >
                <input
                  placeholder="Add a model"
                  value={modelDraft}
                  onChange={(e) => setModelDraft(e.target.value)}
                />
                <button type="submit" className="ghost">
                  Add
                </button>
              </form>
              <div className="row-actions" style={{ marginTop: 16 }}>
                <button
                  type="button"
                  className="ghost danger"
                  disabled={saving}
                  onClick={() => {
                    patchMake(editing.id, { is_offered: false });
                    setEditId('');
                  }}
                >
                  Stop offering
                </button>
              </div>
            </>
          )}
        </Sheet>
      )}
      {addingBrand && (
        <Sheet title="Offer a brand" subtitle="Brand catalog" variant="modal" wide onClose={() => setAddingBrand(false)}>
          <p className="lede" style={{ marginTop: 0 }}>
            Pick from the catalog, or type a name that is not listed.
          </p>
          <input
            className="search"
            placeholder="Search catalog…"
            value={catalogQuery}
            onChange={(e) => setCatalogQuery(e.target.value)}
            style={{ maxWidth: 'none' }}
          />
          <div className="brand-pick-list">
            {catalog.length === 0 ? (
              <p className="empty">No more catalog brands match.</p>
            ) : (
              catalog.map((make) => (
                <button
                  key={make.id}
                  type="button"
                  className="brand-pick"
                  onClick={() => offerMake(make)}
                >
                  <BrandMark name={make.name} src={make.logo_url} />
                  <span>{make.name}</span>
                </button>
              ))
            )}
          </div>
          <form onSubmit={addCustomBrand}>
            <label>
              Or add a custom brand
              <input
                value={brandDraft}
                onChange={(e) => setBrandDraft(e.target.value)}
                placeholder="e.g. Bestune"
              />
            </label>
            <div className="row-actions" style={{ marginTop: 12 }}>
              <button type="submit" className="primary" disabled={!brandDraft.trim()}>
                Add custom
              </button>
              <button type="button" className="ghost" onClick={() => setAddingBrand(false)}>
                Cancel
              </button>
            </div>
          </form>
        </Sheet>
      )}
    </>
  );
}
